// Type definitions for the rune data structure
export interface RuneStats {
  // Style IDs and keys
  primary_style_id: number;
  primary_style: string;
  primary_perk_ids: string;
  primary_perks: string;
  secondary_style_id: number;
  secondary_style: string;
  secondary_perk_ids: string;
  secondary_perks: string;
  
  // Shard IDs and keys
  shard_offense_id: number;
  shard_offense: string;
  shard_flex_id: number;
  shard_flex: string;
  shard_defense_id: number;
  shard_defense: string;
  
  // Stats
  play_count: number;
  wins: number;
  win_rate_pct: number;
  
  // Parsed arrays (to be added in component)
  primary_perk_ids_array?: number[];
  secondary_perk_ids_array?: number[];
}

// Rune tree configuration
export interface RuneTreeConfig {
  treeImage: string;
  KEYSTONES: string[];
  SLOT1: string[];
  SLOT2: string[];
  SLOT3: string[];
}

export interface RunePathsConfig {
  [key: string]: RuneTreeConfig;
}

// Shard configuration
export type ShardPathKeys = 'ROW1' | 'ROW2' | 'ROW3';
export type ShardNames = string;

// Define rune trees
export const RUNE_TREES: RunePathsConfig = {
  Domination: {
    treeImage: 'https://ddragon.canisback.com/img/perk-images/Styles/7200_Domination.png',
    KEYSTONES: [
      'Electrocute',
      'Dark Harvest',
      'Hail of Blades'
    ],
    SLOT1: [
      'Cheap Shot',
      'Taste of Blood',
      'Sudden Impact',
    ],
    SLOT2: [
      'Sixth Sense',
      'Grisly Mementos',
      'Deep Ward',
    ],
    SLOT3: [
      'Treasure Hunter',
      'Relentless Hunter',
      'Ultimate Hunter',
    ],
  },
  Precision: {
    treeImage: 'https://ddragon.canisback.com/img/perk-images/Styles/7201_Precision.png',
    KEYSTONES: [
      'Press the Attack',
      'Lethal Tempo',
      'Fleet Footwork',
      'Conqueror',
    ],
    SLOT1: [
      'Absorb Life',
      'Triumph',
      'Presence of Mind',
    ],
    SLOT2: [
      'Legend: Alacrity',
      'Legend: Haste',
      'Legend: Bloodline',
    ],
    SLOT3: [
      'Coup de Grace',
      'Cut Down',
      'Last Stand',
    ],
  },
  Sorcery: {
    treeImage: 'https://ddragon.canisback.com/img/perk-images/Styles/7202_Sorcery.png',
    KEYSTONES: [
      'Summon Aery',
      'Arcane Comet',
      'Phase Rush',
    ],
    SLOT1: [
      'Nimbus Cloak',
      'Manaflow Band',
      'Axiom Arcanist',
    ],
    SLOT2: [
      'Transcendence',
      'Celerity',
      'Absolute Focus',
    ],
    SLOT3: [
      'Scorch',
      'Waterwalking',
      'Gathering Storm',
    ],
  },
  Resolve: {
    treeImage: 'https://ddragon.canisback.com/img/perk-images/Styles/7204_Resolve.png',
    KEYSTONES: [
      'Grasp of the Undying',
      'Aftershock',
      'Guardian',
    ],
    SLOT1: [
      'Demolish',
      'Font of Life',
      'Shield Bash',
    ],
    SLOT2: [
      'Conditioning',
      'Second Wind',
      'Bone Plating',
    ],
    SLOT3: [
      'Overgrowth',
      'Revitalize',
      'Unflinching',
    ],
  },
  Inspiration: {
    treeImage: 'https://ddragon.canisback.com/img/perk-images/Styles/7203_Whimsy.png',
    KEYSTONES: [
      'Glacial Augment',
      'Unsealed Spellbook',
      'First Strike',
    ],
    SLOT1: [
      'Hextech Flashtraption',
      'Magical Footwear',
      'Cash Back',
    ],
    SLOT2: [
      'Triple Tonic',
      'Time Warp Tonic',
      'Biscuit Delivery',
    ],
    SLOT3: [
      'Cosmic Insight',
      'Approach Velocity',
      'Jack Of All Trades',
    ],
  }
};

export type RuneSelection = {
  primaryPath: string;
  primaryRunes: string[];
  secondaryPath: string;
  secondaryRunes: string[];
  shards: string[];
};

// Define shard paths
export const SHARD_PATHS: Record<ShardPathKeys, ShardNames[]> = {
  ROW1: [
    'adaptiveForce',
    'attackSpeed',
    'abilityHaste'
  ],
  ROW2: [
    'adaptiveForce',
    'moveSpeed',
    'healthScaling'
  ],
  ROW3: [
    'health',
    'tenacity',
    'healthScaling'
  ],
};

// Constant rune images and configurations
export const RUNE_TREE_IMAGES: Record<string, string> = {
  'precision': 'https://ddragon.canisback.com/img/perk-images/Styles/7201_Precision.png',
  'domination': 'https://ddragon.canisback.com/img/perk-images/Styles/7200_Domination.png',
  'sorcery': 'https://ddragon.canisback.com/img/perk-images/Styles/7202_Sorcery.png',
  'resolve': 'https://ddragon.canisback.com/img/perk-images/Styles/7204_Resolve.png',
  'inspiration': 'https://ddragon.canisback.com/img/perk-images/Styles/7203_Whimsy.png'
};

export const SHARD_IMAGES: Record<string, string> = {
  'adaptiveForce': 'https://raw.communitydragon.org/pbe/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/statmods/statmodsadaptiveforceicon.png',
  'attackSpeed': 'https://raw.communitydragon.org/pbe/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/statmods/statmodsattackspeedicon.png',
  'abilityHaste': 'https://raw.communitydragon.org/pbe/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/statmods/statmodscdrscalingicon.png',
  'armor': 'https://raw.communitydragon.org/pbe/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/statmods/statmodsarmoricon.png',
  'magicResist': 'https://raw.communitydragon.org/pbe/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/statmods/statmodsmagicresicon.png',
  'healthScaling': 'https://raw.communitydragon.org/pbe/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/statmods/statmodshealthplusicon.png',
  'health': 'https://raw.communitydragon.org/pbe/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/statmods/statmodshealthscalingicon.png',
  'moveSpeed': 'https://raw.communitydragon.org/pbe/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/statmods/statmodsmovementspeedicon.png',
  'tenacity': 'https://raw.communitydragon.org/pbe/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/statmods/statmodstenacityicon.png'
};

// Map style names to tree keys
export const styleToTreeKey: Record<string, string> = {
  'domination': 'Domination',
  'inspiration': 'Inspiration',
  'precision': 'Precision',
  'resolve': 'Resolve',
  'sorcery': 'Sorcery'
};

// Map rune names to their slots for secondary path selection
export const runeToSlotMapping: Record<string, string> = {
  // Precision tree
  "Absorb Life": "slot1",
  "Triumph": "slot1",
  "Presence of Mind": "slot1",
  "Legend: Alacrity": "slot2",
  "Legend: Haste": "slot2",
  "Legend: Bloodline": "slot2",
  "Coup de Grace": "slot3",
  "Cut Down": "slot3",
  "Last Stand": "slot3",

  // Domination tree
  "Cheap Shot": "slot1",
  "Taste of Blood": "slot1",
  "Sudden Impact": "slot1",
  "Sixth Sense": "slot2",
  "Grisly Mementos": "slot2",
  "Deep Ward": "slot2",
  "Treasure Hunter": "slot3",
  "Relentless Hunter": "slot3",
  "Ultimate Hunter": "slot3",

  // Sorcery tree
  "Nimbus Cloak": "slot1",
  "Manaflow Band": "slot1",
  "Axiom Arcanist": "slot1",
  "Transcendence": "slot2",
  "Celerity": "slot2",
  "Absolute Focus": "slot2",
  "Scorch": "slot3",
  "Waterwalking": "slot3",
  "Gathering Storm": "slot3",
  
  // Resolve tree
  "Demolish": "slot1",
  "Font of Life": "slot1",
  "Shield Bash": "slot1",
  "Conditioning": "slot2",
  "Second Wind": "slot2",
  "Bone Plating": "slot2",
  "Overgrowth": "slot3",
  "Revitalize": "slot3",
  "Unflinching": "slot3",
  
  // Inspiration tree
  "Hextech Flashtraption": "slot1",
  "Magical Footwear": "slot1",
  "Cash Back": "slot1",
  "Triple Tonic": "slot2",
  "Time Warp Tonic": "slot2",
  "Biscuit Delivery": "slot2",
  "Cosmic Insight": "slot3",
  "Approach Velocity": "slot3",
  "Jack Of All Trades": "slot3"
};


